import React, { useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { useAppDispatch } from '@/store';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { NOTIFICATION_COLORS, NOTIFICATION_UNREAD_DOT_DESKTOP } from '@/constants/residents/mobile/notificationColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { strings } from '@/constants/residents/strings';
import { setSelectedBulletinId } from '@/store/slices/residents/homeSlice';
import { NOTIFICATIONS_MAX_MARK } from '@/constants/residents/notifications';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { NotificationList } from '@/components/residents/notification/NotificationList';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';
import { 
  isMarketplaceRelated, 
  isPropertyChat as isPropertyChatHelper,
  getListingImageUrl,
} from '@/utils/residents/notificationHelpers';

type Props = {
  onClose: () => void;
  navigateToAll: () => void;
  navigateToSettings?: () => void;
  navigateToChat?: () => void;
};

export const DesktopNotificationsPopover: React.FC<Props> = ({ onClose, navigateToAll, navigateToSettings, navigateToChat }) => {
  const dispatch = useAppDispatch();
  const { notifications, isLoading, isUnread, markFirstNAsRead, formatDateMMMdd, isMarketplaceFeedback, reload, markPropertyChatAsViewed } = useNotifications();
  
  // Use consolidated marketplace feedback modal hook
  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  React.useEffect(() => {
    if (Array.isArray(notifications) && notifications.length > 0) {
      markFirstNAsRead(notifications, NOTIFICATIONS_MAX_MARK);
    }
  }, [notifications, markFirstNAsRead]);

  // Handle notification item press
  const handleNotificationPress = useCallback((b: NotificationItem) => {
    if (isMarketplaceFeedback(b)) {
      feedbackModal.openModal(b);
    } else if (isPropertyChatHelper(b)) {
      // Optimistically mark property chat as viewed for instant UI feedback
      markPropertyChatAsViewed();
      // Navigate to chat/messages screen for property chat notifications
      onClose();
      if (navigateToChat) {
        navigateToChat();
      }
    } else {
      dispatch(setSelectedBulletinId(b.id));
      navigateToAll();
    }
  }, [dispatch, navigateToAll, navigateToChat, onClose, isMarketplaceFeedback, feedbackModal, markPropertyChatAsViewed]);

  const renderRow = (b: NotificationItem) => {
    const isPropertyChatItem = isPropertyChatHelper(b);
    const isMarketplace = isMarketplaceRelated(b);
    
    // Get listing image for marketplace notifications using helper
    const listingImageUrl = getListingImageUrl(b);
    
    // Select icon based on notification type
    const IconComponent = isPropertyChatItem
      ? RESIDENTS_SVG_COMPONENTS.CHAT_BUBBLE
      : isMarketplace 
        ? RESIDENTS_SVG_COMPONENTS.SUPERMARKET 
        : RESIDENTS_SVG_COMPONENTS.PROPERTY;
      
    return (
      <TouchableOpacity 
        key={String(b.id)} 
        accessibilityRole="button" 
        onPress={() => handleNotificationPress(b)} 
        style={styles.row}
      >
        <View style={styles.avatarWrap}>
          <View style={styles.avatarCircle}>
            {listingImageUrl ? (
              <Image 
                source={{ uri: listingImageUrl }} 
                style={styles.avatarImage} 
                resizeMode="cover"
              />
            ) : (
              <IconComponent width={22} height={22} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE}/>
            )}
            {isUnread(b) && (
              <View style={styles.unreadBubble} accessibilityLabel="Unread notification" />
            )}
          </View>
        </View>
        <View style={styles.rowTextWrap}>
          <Text numberOfLines={1} style={styles.rowTitle}>{b.title || b.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
          <Text numberOfLines={2} style={styles.rowSub}>{b.description || b.descriptionHtml || ''}</Text>
        </View>
        <View style={styles.rowDateWrap}>
          <Text style={styles.rowDate}>{formatDateMMMdd(b.createdAt)}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.wrapper}>
      {/* Outside click overlay for web to close */}
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose} />
      <View style={styles.popover}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{strings.notifications.desktop.title}</Text>
          <TouchableOpacity accessibilityRole="button" accessibilityLabel={strings.notifications.settings.title} onPress={() => (navigateToSettings ? navigateToSettings() : navigateToAll())} style={styles.settingsButton}>
            <RESIDENTS_SVG_COMPONENTS.COG width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY} />
          </TouchableOpacity>
        </View>
        <View style={styles.divider} />
        <View style={styles.listWrap}>
          <NotificationList
            data={notifications}
            isLoading={isLoading}
            emptyText={strings.notifications.desktop.noNew}
            onPressItem={handleNotificationPress}
            showDateSmall={false}
            rowVariant={'popover'}
            showSeparators={false}
            renderRight={(b: NotificationItem) => (
              <View style={{ width: 52, alignItems: 'flex-end' }}>
                <Text style={{ color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, fontSize: 12 }}>{formatDateMMMdd(b.createdAt)}</Text>
              </View>
            )}
          />
        </View>
        <View style={styles.footer}>
          <TouchableOpacity accessibilityRole="button" onPress={navigateToAll}>
            <Text style={styles.footerLink}>{strings.notifications.desktop.seeAll}</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Marketplace Feedback Modal */}
      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: 40,
    right: 0,
    zIndex: 1100,
    elevation: 24,
  },
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  popover: {
    width: 400,
    maxHeight: 560,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  headerTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    fontSize: 16,
  },
  settingsButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  settingsText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
    fontSize: 12,
  },
  divider: {
    height: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  listWrap: {
    flexGrow: 1,
    maxHeight: 460,
  },
  loadingWrap: {
    paddingVertical: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 8,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  emptyWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    gap: 8,
  },
  emptyTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
  },
  emptySub: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
  },
  scroll: {
    maxHeight: 460,
  },
  scrollContent: {
    paddingVertical: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  avatarWrap: {
    paddingRight: 8,
  },
  avatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  unreadBubble: {
    position: 'absolute',
    top: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetTop,
    left: NOTIFICATION_UNREAD_DOT_DESKTOP.offsetLeft,
    width: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
    height: NOTIFICATION_UNREAD_DOT_DESKTOP.size,
    borderRadius: NOTIFICATION_UNREAD_DOT_DESKTOP.borderRadius,
    backgroundColor: NOTIFICATION_COLORS.unreadDot,
    borderWidth: 1,
    borderColor: NOTIFICATION_COLORS.unreadDot,
  },
  rowTextWrap: {
    flex: 1,
    paddingRight: 8,
  },
  rowTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    fontSize: 14,
  },
  rowSub: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
    marginTop: 2,
  },
  rowDateWrap: {
    width: 52,
    alignItems: 'flex-end',
  },
  rowDate: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
  },
  footer: {
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  footerLink: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    fontSize: 12,
    fontWeight: '600',
  },
});

export default DesktopNotificationsPopover;


